/**
 * Player List View - Global player management interface
 * Uses centralized layout system
 */

import { apiService } from "../services/api.js";
import { Player } from "../types/index.js";
import { showNotification, renderLoadingState } from "../utils/ui.js";
import { AddPlayerModal } from "../components/AddPlayerModal.js";
import { EditPlayerModal } from "../components/EditPlayerModal.js";
import { applyBaseLayout, setupHeaderListeners } from "../layouts/LayoutManager";
import { t } from "../i18n/config";

export class PlayerListView {
  private container: HTMLElement;
  private players: Player[] = [];
  private addPlayerModal: AddPlayerModal;
  private editPlayerModal: EditPlayerModal;

  constructor(container: HTMLElement) {
    this.container = container;
    this.addPlayerModal = new AddPlayerModal();
    this.editPlayerModal = new EditPlayerModal();
  }

  async render() {
    try {
      const contentHTML = `
        <div class="space-y-8">
          <!-- Page Header -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h1 class="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white mb-3">${t("players.title")}</h1>
              <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-lg">${t("players.subtitle")}</p>
            </div>
            <button id="add-player-btn" class="btn-primary">
              <span class="material-symbols-outlined">person_add</span>
              <span>${t("players.addBtn")}</span>
            </button>
          </div>

          <!-- Players Table -->
          <div class="card overflow-hidden">
            <div id="players-table-container">
              ${renderLoadingState(t("players.loading"))}
            </div>
          </div>
        </div>
      `;

      const layoutHTML = applyBaseLayout({
        activePage: "players",
        container: this.container,
        content: contentHTML,
        title: "Players",
        description: "Manage global players and their tournament participation."
      });

      this.container.innerHTML = layoutHTML;
      await this.loadPlayers();
      this.attachEventListeners();
    } catch (error) {
      console.error("Error rendering PlayerListView:", error);
      showNotification(t("players.loadFailed"), "error");
    }
  }

  private async loadPlayers() {
    const data = await apiService.getAllPlayers();
    if (data.success) {
      this.players = data.data || [];
      this.renderPlayersTable();
    } else {
      throw new Error(data.error || "Failed to load players");
    }
  }

  private renderPlayersTable() {
    const tableContainer = this.container.querySelector("#players-table-container");
    if (!tableContainer) return;

    if (this.players.length === 0) {
      tableContainer.innerHTML = `
        <div class="text-center py-12">
          <span class="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 mb-4 block">groups</span>
          <h3 class="text-2xl font-bold text-slate-900 dark:text-white mb-2">${t("players.empty.title")}</h3>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-6">${t("players.empty.body")}</p>
          <button id="empty-add-player-btn" class="btn-primary">
            <span class="material-symbols-outlined">add</span>
            <span>${t("players.addBtn")}</span>
          </button>
        </div>
      `;

      this.container.querySelector("#empty-add-player-btn")?.addEventListener("click", () => {
        this.showAddPlayerModal();
      });

      return;
    }

    tableContainer.innerHTML = `
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken border-b border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
            <tr>
              <th class="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">${t("players.table.name")}</th>
              <th class="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">${t("players.table.level")}</th>
              <th class="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">${t("players.table.joined")}</th>
              <th class="px-6 py-4 text-right text-sm font-semibold text-slate-900 dark:text-white">${t("players.table.actions")}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700 daylight:divide-day-ink/10">
            ${this.players.map(player => `
              <tr class="hover:bg-slate-50 dark:hover:bg-slate-800/50 daylight:hover:bg-day-sunken transition">
                <td class="px-6 py-4 text-sm text-slate-900 dark:text-white font-medium">${player.name}</td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${player.level ?? 0}</td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">
                  ${new Date(player.createdAt).toLocaleDateString()}
                </td>
                <td class="px-6 py-4 text-right">
                  <button class="edit-player-btn text-primary daylight:text-day-ink hover:text-primary/80 daylight:hover:text-day-ink-soft font-medium text-sm" data-player-id="${player.id}">
                    ${t("players.table.edit")}
                  </button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private attachEventListeners() {
    // Setup header listeners
    setupHeaderListeners(this.container, {
      onLogout: () => {
        localStorage.removeItem("authToken");
        window.location.hash = "#login";
      }
    });

    // Add Player Button
    this.container.querySelector("#add-player-btn")?.addEventListener("click", () => {
      this.showAddPlayerModal();
    });

    // Edit button listeners
    this.container.querySelectorAll(".edit-player-btn").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const playerId = (e.currentTarget as HTMLElement).getAttribute("data-player-id");
        if (playerId) {
          const player = this.players.find(p => p.id === parseInt(playerId));
          if (player) {
            await this.showEditPlayerModal(player);
          }
        }
      });
    });
  }

  private async showAddPlayerModal() {
    const result = await this.addPlayerModal.show();
    if (result) {
      try {
        const response = await apiService.createPlayer(result);
        if (response.success) {
          showNotification(t("players.addSuccess"), "success");
          await this.loadPlayers();
        } else {
          showNotification(response.error || t("players.addFailed"), "error");
        }
      } catch (error) {
        console.error("Error adding player:", error);
        showNotification(t("players.addFailed"), "error");
      }
    }
  }

  private async showEditPlayerModal(player: Player) {
    const result = await this.editPlayerModal.show(player);
    if (result) {
      try {
        const response = await apiService.updatePlayer(player.id, result);
        if (response.success) {
          showNotification(t("players.updateSuccess"), "success");
          await this.loadPlayers();
        } else {
          showNotification(response.error || t("players.updateFailed"), "error");
        }
      } catch (error) {
        console.error("Error updating player:", error);
        showNotification(t("players.updateFailed"), "error");
      }
    }
  }
}